// The arithmetic behind the Meeting Management summary. Pure functions over the
// three record shapes, so the screen only lays the answers out.
//
// Each resource keeps its business status in its own column (meetingStatus,
// minuteStatus, actionStatus) — `status` only says ACTIVE or VOID.

import {
  countBy, totalBy, sumOf, lastMonths, upcoming, inYear, daysUntil, dateOf, isoOf, percent
} from '../../utils/summaryStats';
import type { NamedCount, NamedTotal, MonthBucket } from '../../utils/summaryStats';

export interface MeetingLike {
  recordId?: string;
  title?: string;
  type?: string;
  committee?: string;
  date?: string;
  time?: string;
  venue?: string;
  /** Scheduled / Completed / Cancelled. */
  meetingStatus?: string;
  status?: string;
}

export interface MinuteLike {
  recordId?: string;
  mtgId?: string;
  agendaTopic?: string;
  resolutionNo?: string;
  budget?: unknown;
  /** Approved / Rejected / Deferred. */
  minuteStatus?: string;
  dateRecorded?: string;
  status?: string;
}

export interface ActionLike {
  recordId?: string;
  minId?: string;
  task?: string;
  assignedTo?: string;
  dueDate?: string;
  /** Pending / In Progress / Completed. */
  actionStatus?: string;
  status?: string;
}

const isVoid = (r: { status?: string }, own?: string) =>
  String(r.status || '').toUpperCase() === 'VOID' || String(own || '').toUpperCase() === 'VOID';

/** A meeting with no status yet counts as Scheduled, as the form defaults it. */
export const meetingStatusOf = (m: MeetingLike): string => String(m.meetingStatus || '').trim() || 'Scheduled';
/** A resolution with no status yet counts as Approved, as the form defaults it. */
export const minuteStatusOf = (m: MinuteLike): string => String(m.minuteStatus || '').trim() || 'Approved';
/** An action with no status yet counts as Pending, as the form defaults it. */
export const actionStatusOf = (a: ActionLike): string => String(a.actionStatus || '').trim() || 'Pending';

export const isOpenAction = (a: ActionLike): boolean => !/^(completed|done|closed)$/i.test(actionStatusOf(a));

export interface OverdueAction extends ActionLike {
  daysOverdue: number;
  /** The agenda topic of the resolution the action came from. */
  topic?: string;
}

export interface PersonActions {
  name: string;
  open: number;
  overdue: number;
  done: number;
  total: number;
}

export interface MeetingSummary {
  meetingsThisYear: number;
  upcoming: MeetingLike[];
  /** Meetings whose date has passed but are still marked Scheduled. */
  notClosed: MeetingLike[];
  months: MonthBucket[];
  byStatus: NamedCount[];
  byCommittee: NamedCount[];
  resolutions: number;
  resolutionsByStatus: NamedTotal[];
  approved: number;
  budgetApproved: number;
  budgetApprovedThisYear: number;
  openActions: number;
  dueSoon: number;
  overdue: OverdueAction[];
  completionRate: number;
  actionTotal: number;
  byPerson: PersonActions[];
}

export const meetingSummary = (
  meetings: MeetingLike[], minutes: MinuteLike[], actions: ActionLike[], now: Date = new Date()
): MeetingSummary => {
  const mtgs = meetings.filter((m) => !isVoid(m, m.meetingStatus));
  const mins = minutes.filter((m) => !isVoid(m, m.minuteStatus));
  const acts = actions.filter((a) => !isVoid(a, a.actionStatus));
  const today = isoOf(now);
  const scheduled = mtgs.filter((m) => meetingStatusOf(m) === 'Scheduled');
  const approved = mins.filter((m) => minuteStatusOf(m) === 'Approved');
  const topicOf = new Map<string | undefined, string | undefined>(mins.map((m) => [m.recordId, m.agendaTopic]));

  const open = acts.filter(isOpenAction);
  const withDue = open.map((a) => ({ a, due: daysUntil(a.dueDate, now) }));
  const overdue: OverdueAction[] = withDue
    .filter((x) => x.due !== null && x.due < 0)
    .map((x) => ({ ...x.a, daysOverdue: -(x.due as number), topic: topicOf.get(x.a.minId) }))
    .sort((a, b) => b.daysOverdue - a.daysOverdue);

  const people = new Map<string, PersonActions>();
  acts.forEach((a) => {
    const k = String(a.assignedTo || '').trim() || 'Not assigned';
    const cur = people.get(k) || { name: k, open: 0, overdue: 0, done: 0, total: 0 };
    cur.total += 1;
    if (!isOpenAction(a)) cur.done += 1;
    else {
      cur.open += 1;
      const due = daysUntil(a.dueDate, now);
      if (due !== null && due < 0) cur.overdue += 1;
    }
    people.set(k, cur);
  });

  // A resolution is dated by the meeting it was passed at, else when it was recorded.
  const meetingDate = new Map<string | undefined, string | undefined>(mtgs.map((m) => [m.recordId, m.date]));
  const minuteDate = (m: MinuteLike) => meetingDate.get(m.mtgId) || m.dateRecorded;

  return {
    meetingsThisYear: inYear(mtgs, (m) => m.date, now.getFullYear()).length,
    upcoming: upcoming(scheduled, (m) => m.date, 60, now),
    notClosed: scheduled
      .filter((m) => { const d = dateOf(m.date); return d !== null && d < today; })
      .sort((a, b) => String(a.date).localeCompare(String(b.date))),
    months: lastMonths(mtgs.filter((m) => (dateOf(m.date) || '') <= today), (m) => m.date, 12, now),
    byStatus: countBy(mtgs, meetingStatusOf),
    byCommittee: countBy(mtgs, (m) => m.committee),
    resolutions: mins.length,
    resolutionsByStatus: totalBy(mins, minuteStatusOf, (m) => m.budget),
    approved: approved.length,
    budgetApproved: sumOf(approved, (m) => m.budget),
    budgetApprovedThisYear: sumOf(inYear(approved, minuteDate, now.getFullYear()), (m) => m.budget),
    openActions: open.length,
    dueSoon: withDue.filter((x) => x.due !== null && x.due >= 0 && x.due <= 7).length,
    overdue,
    completionRate: percent(acts.length - open.length, acts.length),
    actionTotal: acts.length,
    byPerson: Array.from(people.values())
      .sort((a, b) => b.open - a.open || b.total - a.total || a.name.localeCompare(b.name))
  };
};
